// The render service shapes (ADR-024, proposed contracts 1.1, section 8; E31-S3-T1). Moved here from
// apps/foundry/src/api/schema.ts unchanged in substance; the foundry now re-exports them.
//
// The one rule: every object is STRICT. An unknown key anywhere is refused. A request can carry
// pack-derived script, stills metadata and theme, and nothing else — no field here can hold a
// ScreenContextFrame, a RecordEntry, a transcript or a Principal (ADR-020's isolation claim, made
// mechanical). The RenderPort that carries them is an interface in @waypoint/ports.
import { z } from "zod";
import { Citation, Pages } from "./citation.js";
import { Count, Id, IsoUtc, NonEmpty, strictObject, version } from "./primitives.js";
import { Sha256 } from "./training.js";
/** A theme colour: #rrggbb. */
const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb");
/** A rectangle in a still's pixels: a zoom region or a highlight box. */
export const Box = strictObject({
    x: Count,
    y: Count,
    w: z.number().int().positive(),
    h: z.number().int().positive(),
});
const Narration = z.string().trim().min(1).max(4000);
export const Scene = z.discriminatedUnion("kind", [
    strictObject({
        kind: z.literal("title"),
        text: NonEmpty,
        narration: Narration,
        citation: Citation,
    }),
    strictObject({ kind: z.literal("avatar"), narration: Narration, citation: Citation }),
    strictObject({
        kind: z.literal("still"),
        stillId: Id,
        narration: Narration,
        citation: Citation,
        zoom: Box.optional(),
        box: Box.optional(),
        kicker: NonEmpty.max(80).optional(),
        statement: NonEmpty.max(160).optional(),
        segment: z.number().int().positive().optional(),
    }),
    strictObject({
        kind: z.literal("video"),
        stillId: Id,
        narration: Narration,
        citation: Citation,
        fit: z.literal("fit_to_scene"),
    }),
]);
export const Script = strictObject({ v: version(1), scenes: z.array(Scene).min(1).max(60) });
/** No background colour, by design: the canvas is white and the palettes colour text, rules and the box. */
export const Theme = strictObject({
    name: NonEmpty,
    document: strictObject({ heading: Hex, link: Hex, rule: Hex }),
    application: strictObject({ nav: Hex, panel: Hex, accent: Hex }),
    logoStillId: Id.optional(),
});
/** One still the request declares; its bytes are uploaded separately and checked against sha256. */
export const RenderStill = strictObject({
    id: Id,
    sha256: Sha256,
    bytes: z.number().int().positive(),
    contentType: z.enum(["image/png", "image/jpeg"]),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
const inside = (b, o) => b.x >= o.x && b.y >= o.y && b.x + b.w <= o.x + o.w && b.y + b.h <= o.y + o.h;
export const RenderRequest = strictObject({
    v: version(1),
    idempotencyKey: NonEmpty.max(128),
    title: strictObject({ id: Id, label: NonEmpty, guidePages: Pages }),
    script: Script,
    theme: Theme,
    narration: strictObject({
        wordsPerMinute: z.literal(200),
        voiceRef: NonEmpty.optional(),
        avatarRef: NonEmpty.optional(),
    }),
    captions: strictObject({ burnIn: z.boolean() }),
    questions: z.union([
        strictObject({ generate: z.literal(true) }),
        strictObject({ provided: z.string().min(1) }),
    ]),
    stills: z.array(RenderStill).max(100),
    webhook: strictObject({ url: z.url() }).optional(),
}).superRefine((r, ctx) => {
    const ids = new Set();
    const sizes = new Map();
    r.stills.forEach((s, i) => {
        if (ids.has(s.id))
            ctx.addIssue({
                code: "custom",
                message: `duplicate still id ${s.id}`,
                path: ["stills", i, "id"],
            });
        ids.add(s.id);
        sizes.set(s.id, s);
    });
    r.script.scenes.forEach((s, i) => {
        if ((s.kind === "still" || s.kind === "video") && !ids.has(s.stillId))
            ctx.addIssue({
                code: "custom",
                message: `scene ${String(i)} names still ${s.stillId}, which the request does not declare`,
                path: ["script", "scenes", i, "stillId"],
            });
        // A zoom or box outside what the frame shows would be drawn over the margins or the caption band.
        const size = s.kind === "still" ? sizes.get(s.stillId) : undefined;
        if (s.kind !== "still" || !size)
            return;
        const still = { x: 0, y: 0, w: size.width, h: size.height };
        if (s.zoom && !inside(s.zoom, still))
            ctx.addIssue({
                code: "custom",
                message: `scene ${String(i)}: zoom lies outside still ${s.stillId} (${String(size.width)}×${String(size.height)})`,
                path: ["script", "scenes", i, "zoom"],
            });
        if (s.box && !inside(s.box, s.zoom ?? still))
            ctx.addIssue({
                code: "custom",
                message: `scene ${String(i)}: box lies outside ${s.zoom ? "the zoom region" : `still ${s.stillId}`}`,
                path: ["script", "scenes", i, "box"],
            });
    });
    if (r.theme.logoStillId !== undefined && !ids.has(r.theme.logoStillId))
        ctx.addIssue({
            code: "custom",
            message: `theme names still ${r.theme.logoStillId}, which the request does not declare`,
            path: ["theme", "logoStillId"],
        });
});
export const RenderJobState = z.enum([
    "created",
    "queued",
    "rendering",
    "done",
    "failed",
    "cancelled",
]);
/** A rendered asset's digest. Fetched through the port by name; never a URL. */
export const RenderAsset = strictObject({ sha256: Sha256, bytes: Count, contentType: NonEmpty });
/** The assets a done job holds, by the name RenderPort.fetch takes. */
export const RenderAssetName = z.enum(["video", "captionedVideo", "captions", "questions"]);
export const RenderError = strictObject({
    code: z.enum([
        "provider-failed",
        "provider-timeout",
        "still-missing",
        "invalid-script",
        "cancelled",
        "quota",
    ]),
    message: NonEmpty,
});
export const RenderJobResult = strictObject({
    video: RenderAsset,
    captionedVideo: RenderAsset.optional(),
    captions: RenderAsset,
    questions: RenderAsset,
    durationSeconds: z.number().positive(),
    scenes: z.array(strictObject({
        index: Count,
        start: z.number().nonnegative(),
        end: z.number().nonnegative(),
    })),
});
export const RenderJob = strictObject({
    v: version(1),
    id: Id,
    state: RenderJobState,
    progress: strictObject({
        step: z.enum(["compositing", "uploading", "rendering", "captions", "questions"]),
        pct: z.number().min(0).max(100).optional(),
    }).optional(),
    createdAt: IsoUtc,
    updatedAt: IsoUtc,
    /** Assets are deleted at expiresAt (seven days after done). */
    expiresAt: IsoUtc,
    result: RenderJobResult.optional(),
    error: RenderError.optional(),
});
/** ADR-024 §4: the webhook body. A prompt to poll, never the result. */
export const RenderWebhookEvent = strictObject({
    v: version(1),
    jobId: Id,
    state: RenderJobState,
    at: IsoUtc,
});
//# sourceMappingURL=render.js.map